import { useState } from 'react';
import { Calendar, Clock, MapPin, Users, AlertCircle, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { upcomingEvents } from '@data/siteData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/Card';
import Badge from '@components/ui/Badge';
import Button from '@components/ui/Button';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import type { Event } from '../types';
import { FadeIn } from '@components/animations';

function EventCard({ event, index }: { event: Event; index: number }) {
  const [showDetails, setShowDetails] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  const isBooked = event.status === 'booked';
  const isAvailable = event.status === 'available';

  return (
    <FadeIn delay={index * 0.1} direction="up" distance={40}>
      <motion.div
        whileHover={shouldReduceMotion ? {} : { 
          y: -8, 
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 30px rgba(59, 130, 246, 0.15)'
        }}
        transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        className="h-full"
      >
        <Card className="h-full flex flex-col">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Badge
                  variant={isBooked ? 'error' : isAvailable ? 'success' : 'default'}
                  className="mb-2"
                >
                  {event.type}
                </Badge>
                <CardTitle className="text-lg">{event.title}</CardTitle>
              </div>
              {isBooked && (
                <motion.div 
                  initial={shouldReduceMotion ? {} : { scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="flex items-center gap-1 text-red-400 text-sm font-medium"
                >
                  <AlertCircle size={16} />
                  <span>Ausgebucht</span>
                </motion.div>
              )}
            </div>
            <CardDescription>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm">
                <span className="flex items-center gap-1">
                  <Calendar size={14} className="text-blue-400" />
                  {format(new Date(event.date), 'EEEE, dd. MMMM yyyy', { locale: de })}
                </span>
                <span className="text-slate-600">|</span>
                <span className="flex items-center gap-1 text-slate-300">
                  <Clock size={14} className="text-blue-400" />
                  {event.time}
                </span>
              </div>
              {event.location && (
                <div className="flex items-center gap-1 mt-2 text-sm">
                  <MapPin size={14} className="text-blue-400" />
                  {event.location}
                </div>
              )}
            </CardDescription>
          </CardHeader>

          <CardContent className="flex-1 flex flex-col">
            <p className="text-slate-400 mb-4">{event.description}</p>

            <AnimatePresence>
              {showDetails && event.detailContent && (
                <motion.div
                  initial={shouldReduceMotion ? {} : { opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={shouldReduceMotion ? {} : { opacity: 0, height: 0 }}
                  transition={{ duration: shouldReduceMotion ? 0 : 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                  className="space-y-4 mb-4 pt-4 border-t border-slate-800 overflow-hidden"
                >
                  {event.detailContent.concept && (
                    <FadeIn direction="none" delay={0.1}>
                      <div>
                        <h4 className="text-white font-medium mb-1">Konzept</h4>
                        <p className="text-slate-400 text-sm">{event.detailContent.concept}</p>
                      </div>
                    </FadeIn>
                  )}

                  {event.detailContent.targetGroup && (
                    <FadeIn direction="none" delay={0.15}>
                      <div>
                        <h4 className="text-white font-medium mb-1">Zielgruppe</h4>
                        <p className="text-slate-400 text-sm">{event.detailContent.targetGroup}</p>
                      </div>
                    </FadeIn>
                  )}

                  {event.detailContent.program && (
                    <FadeIn direction="none" delay={0.2}>
                      <div>
                        <h4 className="text-white font-medium mb-2">Programm</h4>
                        <ul className="space-y-1">
                          {event.detailContent.program.map((item: string, idx: number) => (
                            <motion.li 
                              key={idx} 
                              initial={shouldReduceMotion ? {} : { opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: shouldReduceMotion ? 0 : 0.2 + idx * 0.05 }}
                              className="flex items-start gap-2 text-slate-400 text-sm"
                            >
                              <CheckCircle size={14} className="mt-0.5 text-green-500 shrink-0" />
                              {item}
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </FadeIn>
                  )}

                  {event.detailContent.activities && (
                    <FadeIn direction="none" delay={0.2}>
                      <div>
                        <h4 className="text-white font-medium mb-2">Aktivitäten</h4>
                        <ul className="space-y-1">
                          {event.detailContent.activities.map((item: string, idx: number) => (
                            <motion.li 
                              key={idx}
                              initial={shouldReduceMotion ? {} : { opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: shouldReduceMotion ? 0 : 0.2 + idx * 0.05 }}
                              className="flex items-start gap-2 text-slate-400 text-sm"
                            >
                              <CheckCircle size={14} className="mt-0.5 text-green-500 shrink-0" />
                              {item}
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </FadeIn>
                  )}

                  {event.detailContent.notes && (
                    <FadeIn direction="none" delay={0.25}>
                      <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-lg p-4">
                        <h4 className="text-yellow-400 font-medium mb-2 flex items-center gap-2">
                          <AlertCircle size={16} />
                          Wichtige Hinweise
                        </h4>
                        <ul className="space-y-1">
                          {event.detailContent.notes.map((note: string, idx: number) => (
                            <li key={idx} className="text-yellow-200/80 text-sm">
                              • {note}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </FadeIn>
                  )}

                  {event.detailContent.weatherNote && (
                    <FadeIn direction="none" delay={0.25}>
                      <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-4">
                        <p className="text-blue-300 text-sm">{event.detailContent.weatherNote}</p>
                      </div>
                    </FadeIn>
                  )}

                  {event.maxParticipants && (
                    <FadeIn direction="none" delay={0.3}>
                      <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <Users size={16} className="text-blue-400" />
                        <span>Max. {event.maxParticipants} Teilnehmer</span>
                      </div>
                    </FadeIn>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-auto pt-4 flex flex-wrap items-center gap-3">
              <motion.div whileTap={shouldReduceMotion ? {} : { scale: 0.95 }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDetails(!showDetails)}
                >
                  {showDetails ? 'Weniger Details' : 'Mehr Details'}
                </Button>
              </motion.div>

              {event.registration && !isBooked && (
                <motion.div 
                  whileHover={shouldReduceMotion ? {} : { scale: 1.05 }}
                  whileTap={shouldReduceMotion ? {} : { scale: 0.95 }}
                >
                  <a
                    href={`mailto:${event.registration}?subject=Anmeldung: ${event.title}`}
                  >
                    <Button size="sm">Anmelden</Button>
                  </a>
                </motion.div>
              )}
            </div>

            {event.admission && (
              <p className="mt-3 text-sm text-slate-500">{event.admission}</p>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </FadeIn>
  );
}

export default function EventsPage() {
  const [filter, setFilter] = useState<string>('all');
  const shouldReduceMotion = useReducedMotion();

  const eventTypes = ['all', ...new Set(upcomingEvents.map((e) => e.type))];

  const filteredEvents =
    filter === 'all'
      ? upcomingEvents
      : upcomingEvents.filter((e) => e.type === filter);

  return (
    <div className="py-12 bg-slate-950 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <FadeIn direction="up" className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Aktuelles & Termine
          </h1>
          <p className="text-slate-400 max-w-2xl">
            Veranstaltungskalender der Sternwarte Mannheim mit astronomischen Beobachtungen,
            Führungen, Lesungen und Sonderveranstaltungen.
          </p>
        </FadeIn>

        {/* Filter */}
        <FadeIn direction="up" delay={0.1} className="flex flex-wrap gap-2 mb-8">
          {eventTypes.map((type, index) => (
            <motion.button
              key={type}
              onClick={() => setFilter(type)}
              whileHover={shouldReduceMotion ? {} : { scale: 1.05 }}
              whileTap={shouldReduceMotion ? {} : { scale: 0.95 }}
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: shouldReduceMotion ? 0 : index * 0.05 }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === type
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {type === 'all' ? 'Alle' : type}
            </motion.button>
          ))}
        </FadeIn>

        {/* Events Grid */}
        <AnimatePresence mode="wait">
          {filteredEvents.length > 0 ? (
            <motion.div
              key={filter}
              initial={shouldReduceMotion ? {} : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={shouldReduceMotion ? {} : { opacity: 0 }}
              transition={{ duration: shouldReduceMotion ? 0 : 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              {filteredEvents.map((event, index) => (
                <EventCard key={event.id} event={event} index={index} />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={shouldReduceMotion ? {} : { opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={shouldReduceMotion ? {} : { opacity: 0, scale: 0.95 }}
              className="text-center py-16"
            >
              <Calendar size={48} className="mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">
                Keine Veranstaltungen in dieser Kategorie gefunden.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Info Note */}
        <FadeIn direction="up" delay={0.3}>
          <div className="mt-12 bg-blue-900/20 border border-blue-800/50 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-2">Hinweis zu den Veranstaltungen</h3>
            <p className="text-slate-400 text-sm">
              Die Veranstaltungen der Literaturinitiative &quot;LeseZeichen&quot; finden zum Teil in
              den Reiss-Engelhorn-Museen und im Marchivum statt. Bitte beachten Sie die jeweiligen
              Veranstaltungsorte in den Details.
            </p>
          </div>
        </FadeIn>
      </div>
    </div>
  );
}
